import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar as CalendarIcon, AlertTriangle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { colaboradores } from '../../utils/stageHelpers';
import { EVENT_TYPE_OPTIONS } from './EventTypeBadge';
import { useScheduledEvents } from '../../hooks/useScheduledEvents';
import EntitySearchField from './EntitySearchField';

const REQUIRES_CONTEXT = new Set(['meeting', 'site_visit', 'follow_up']);

const PRIORITY_OPTIONS = [
  { value: 'low',      label: 'Baixa' },
  { value: 'medium',   label: 'Média' },
  { value: 'high',     label: 'Alta' },
  { value: 'critical', label: 'Crítica' },
];

const collaboratorOptions = colaboradores.filter((c) => c.value !== 'all');

function toLocalInput(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromLocalInput(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function colaboradorFromEmail(email) {
  if (!email) return '';
  const prefix = email.split('@')[0]?.toLowerCase() || '';
  const found = collaboratorOptions.find((c) => c.value === prefix);
  return found ? found.value : '';
}

export default function EventCreateModal({
  open,
  onClose,
  onCreated,
  initialStart,
  initialEnd,
  initialAllDay = false,
  currentUserEmail,
  prefill = null,
}) {
  const { createEvent, saving } = useScheduledEvents();
  const defaultOwner = useMemo(() => colaboradorFromEmail(currentUserEmail), [currentUserEmail]);

  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('meeting');
  const [priority, setPriority] = useState('medium');
  const [allDay, setAllDay] = useState(initialAllDay);
  const [startAt, setStartAt] = useState(toLocalInput(initialStart));
  const [endAt, setEndAt] = useState(toLocalInput(initialEnd));
  const [ownerColaboradorId, setOwnerColaboradorId] = useState(defaultOwner);
  const [assignedToColaboradorId, setAssignedToColaboradorId] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [description, setDescription] = useState('');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [error, setError] = useState(null);
  // Onda 4 — prefill: links/attendees nascem fora do form e são salvos no submit
  const [prefillLinks, setPrefillLinks] = useState([]);
  const [prefillAttendees, setPrefillAttendees] = useState([]);

  // Onda 5.5 — vínculos REIP escolhidos manualmente
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [selectedOferta, setSelectedOferta] = useState(null);
  const [selectedContato, setSelectedContato] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setTitle(prefill.title || '');
      setEventType(prefill.eventType || 'meeting');
      setPriority(prefill.priority || 'medium');
      setAllDay(!!prefill.allDay);
      setStartAt(toLocalInput(prefill.startAt));
      setEndAt(toLocalInput(prefill.endAt));
      setOwnerColaboradorId(prefill.ownerColaboradorId || defaultOwner);
      setAssignedToColaboradorId('');
      setBusinessUnitId('');
      setDescription(prefill.description || '');
      setPrefillLinks(Array.isArray(prefill.links) ? prefill.links : []);
      setPrefillAttendees(Array.isArray(prefill.attendees) ? prefill.attendees : []);
      setSelectedEmpresa(null);
      setSelectedOferta(null);
      setSelectedContato(null);
      setError(null);
      return;
    }
    setTitle('');
    setEventType('meeting');
    setPriority('medium');
    setAllDay(initialAllDay);
    setStartAt(toLocalInput(initialStart));
    setEndAt(toLocalInput(initialEnd));
    setOwnerColaboradorId(defaultOwner);
    setAssignedToColaboradorId('');
    setBusinessUnitId('');
    setDescription('');
    setPrefillLinks([]);
    setPrefillAttendees([]);
    setSelectedEmpresa(null);
    setSelectedOferta(null);
    setSelectedContato(null);
    setError(null);
  }, [open, initialStart, initialEnd, initialAllDay, defaultOwner, prefill]);

  // Enriquecer attendees externos buscando contatos do REIP por email
  useEffect(() => {
    if (!open || !prefill) return;
    const externals = (prefill.externalEmails || []).filter(Boolean);
    if (externals.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data: matches, error: fetchErr } = await supabase
        .from('contatos')
        .select('id, first_name, last_name, email, empresa_id')
        .in('email', externals);
      if (cancelled || fetchErr) return;
      const byEmail = new Map();
      for (const c of matches || []) {
        if (c.email) byEmail.set(c.email.toLowerCase(), c);
      }
      setPrefillAttendees((current) => current.map((a) => {
        if (a.type !== 'external' || !a.email) return a;
        const match = byEmail.get(a.email.toLowerCase());
        if (!match) return a;
        const fullName = `${match.first_name || ''} ${match.last_name || ''}`.trim() || null;
        return { ...a, contatoId: match.id, name: fullName };
      }));
    })();
    return () => { cancelled = true; };
  }, [open, prefill]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data, error: fetchErr } = await supabase
        .from('business_units')
        .select('id, nome')
        .order('nome');
      if (!cancelled && !fetchErr) setBusinessUnits(data || []);
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Onda 5.5 — quando oferta é selecionada e empresa ainda está vazia,
  // resolver e auto-preencher empresa a partir do empresa_id da oferta.
  useEffect(() => {
    if (!selectedOferta || selectedEmpresa) return;
    const empresaIdFromOferta = selectedOferta.raw?.empresa_id;
    if (!empresaIdFromOferta) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('empresas')
        .select('id, empresa, company_name')
        .eq('id', empresaIdFromOferta)
        .maybeSingle();
      if (cancelled || !data) return;
      setSelectedEmpresa({
        type: 'empresa',
        id: data.id,
        label: data.company_name || data.empresa || data.id,
        sub: null,
        raw: data,
      });
    })();
    return () => { cancelled = true; };
  }, [selectedOferta, selectedEmpresa]);

  if (!open) return null;

  const manualLinks = [];
  if (selectedEmpresa) manualLinks.push({ entityType: 'empresa', entityId: selectedEmpresa.id, relationType: 'subject' });
  if (selectedOferta) manualLinks.push({ entityType: 'oferta', entityId: selectedOferta.id, relationType: 'subject' });
  if (selectedContato) manualLinks.push({ entityType: 'contato', entityId: selectedContato.id, relationType: 'participant' });

  const mergedLinks = (() => {
    const out = [...prefillLinks];
    for (const ml of manualLinks) {
      const dup = out.some((l) => l.entityType === ml.entityType && l.entityId === ml.entityId);
      if (!dup) out.push(ml);
    }
    return out;
  })();

  const hasReipContext = mergedLinks.some((l) => ['empresa', 'oferta', 'contato'].includes(l.entityType));
  const showContextWarning = REQUIRES_CONTEXT.has(eventType) && !hasReipContext;

  const handleSave = async () => {
    setError(null);
    if (!title.trim()) {
      setError('Título é obrigatório');
      return;
    }
    if (!startAt) {
      setError('Data/hora de início é obrigatória');
      return;
    }
    const startISO = fromLocalInput(startAt);
    const endISO = fromLocalInput(endAt);
    if (!startISO) {
      setError('Data/hora de início inválida');
      return;
    }
    if (endISO && new Date(endISO) < new Date(startISO)) {
      setError('Fim não pode ser antes do início');
      return;
    }

    const { data, error: createErr } = await createEvent({
      title,
      description,
      eventType,
      priority,
      allDay,
      startAt: startISO,
      endAt: endISO,
      ownerColaboradorId: ownerColaboradorId || null,
      assignedToColaboradorId: assignedToColaboradorId || null,
      businessUnitId: businessUnitId || null,
      createdByColaboradorId: defaultOwner || null,
      source: prefill?.source || 'manual',
      links: mergedLinks,
      attendees: prefillAttendees,
    });

    if (createErr) {
      setError(createErr);
      return;
    }
    onCreated?.(data);
    onClose?.();
  };

  const inputClass =
    'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <CalendarIcon className="text-blue-600" size={20} />
            Novo evento
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{error}</div>
          )}

          {prefill && (prefillLinks.length > 0 || prefillAttendees.length > 0) && (
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900 space-y-1">
              <div className="font-medium">Prefill a partir do email:</div>
              {prefillAttendees.length > 0 && (
                <div>
                  <span className="text-blue-700">Participantes:</span>{' '}
                  {prefillAttendees.length} ({prefillAttendees.filter((a) => a.type === 'internal').length} equipe · {prefillAttendees.filter((a) => a.type === 'external').length} externos)
                </div>
              )}
              {prefillLinks.length > 0 && (
                <div>
                  <span className="text-blue-700">Vínculos:</span>{' '}
                  {Array.from(new Set(prefillLinks.map((l) => l.entityType))).join(', ')}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Título <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputClass}
              placeholder="Ex: Reunião com EMPRESA — IPIRANGA"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
              <select value={eventType} onChange={(e) => setEventType(e.target.value)} className={inputClass}>
                {EVENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prioridade</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputClass}>
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="allday"
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="rounded border-slate-300"
            />
            <label htmlFor="allday" className="text-sm text-slate-700">Dia inteiro</label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Início <span className="text-red-500">*</span>
              </label>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                value={allDay ? startAt.slice(0, 10) : startAt}
                onChange={(e) => setStartAt(allDay ? `${e.target.value}T00:00` : e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fim</label>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                value={allDay ? (endAt ? endAt.slice(0, 10) : '') : endAt}
                onChange={(e) => setEndAt(allDay && e.target.value ? `${e.target.value}T23:59` : e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Responsável</label>
              <select
                value={ownerColaboradorId}
                onChange={(e) => setOwnerColaboradorId(e.target.value)}
                className={inputClass}
              >
                <option value="">—</option>
                {collaboratorOptions.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Atribuído a</label>
              <select
                value={assignedToColaboradorId}
                onChange={(e) => setAssignedToColaboradorId(e.target.value)}
                className={inputClass}
              >
                <option value="">—</option>
                {collaboratorOptions.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Vertical <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <select
              value={businessUnitId}
              onChange={(e) => setBusinessUnitId(e.target.value)}
              className={inputClass}
            >
              <option value="">—</option>
              {businessUnits.map((bu) => (
                <option key={bu.id} value={bu.id}>{bu.nome}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Notas internas, contexto da reunião..."
            />
          </div>

          {/* Onda 5.5 — Contexto REIP */}
          <div className="border-t border-slate-200 pt-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-600">
              Contexto REIP
            </div>
            {prefillLinks.length > 0 && (
              <div className="text-[11px] text-slate-500">
                {prefillLinks.length} vínculo{prefillLinks.length === 1 ? '' : 's'} já {prefillLinks.length === 1 ? 'herdado' : 'herdados'} do prefill — adicione mais se quiser.
              </div>
            )}
            <EntitySearchField entityType="empresa" value={selectedEmpresa} onChange={setSelectedEmpresa} />
            <EntitySearchField entityType="oferta"  value={selectedOferta}  onChange={setSelectedOferta} />
            <EntitySearchField entityType="contato" value={selectedContato} onChange={setSelectedContato} />

            {showContextWarning && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 flex items-start gap-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>
                  Evento sem contexto REIP. O pós-reunião não conseguirá salvar conhecimento
                  até que você vincule empresa, oferta ou contato.
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || !startAt || saving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <CalendarIcon size={14} />
            {saving ? 'Salvando...' : 'Criar evento'}
          </button>
        </div>
      </div>
    </div>
  );
}
